import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { apiConfig } from '../config/apiConfig';
import { isTouchDevice } from '../util/device-detection';
import { isTestEnvironment } from '../apiCaching/POST-Requests/helperModules/helperFunctions';
import { generateRandomString } from '../util/sessionCorelationID';
import { CryptoWrapper } from '../util/crypto';
import { isWhitelistedAPI } from './logging-whitelisting';
import { extractPageDetails } from './apiTester';

const loggerURLTransformer = axios.create({
    baseURL: process.env.API_URL,
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'apikey': process.env.URL_TRANSFORMER_ERROR_LOG_KEY,
    }
});

// Define a type for API error response data
interface ApiError {
    error: string;
    code: number;
    Message?: string;
    message?: string;
}

// Function to strip API Key
export function removeApiKey(url: string): string {
    const urlObj = new URL(url);
    // Check if 'apikey' parameter exists
    if (urlObj.searchParams.has('apikey')) {
        urlObj.searchParams.delete('apikey');
    }
    return urlObj.toString();
}

// Centralized function to send log requests (for both error and performance)
export async function sendLogRequest(url: string, data: any, testEnvironment: boolean) {
    const request = {
        method: 'POST',
        url,
        data
    };

    try {
        if (testEnvironment) console.log(request);
        const response = await loggerRequest(request);
        if (testEnvironment) console.log('Log sent to URL Transformer:', response);
    } catch (err) {
        if (testEnvironment) console.error('Failed to send log to URL Transformer', err);
    }
}

// Performance benchmarking function
export function performanceBenchmark(url: string, uniqueID:string) {
    const testEnvironment: boolean = isTestEnvironment();
    performance.mark(`${uniqueID}-end`);
    performance.measure(`${uniqueID}-duration`, `${uniqueID}-start`, `${uniqueID}-end`);

    const entries = performance.getEntriesByName(`${uniqueID}-duration`);
    if (entries.length > 0) {
        const duration: number = entries[0].duration || 0;
        if (duration > Number.parseInt(process.env.API_LOGGER_THRESHOLD ?? '5000', 10)) {
            callPerformanceLogger(url, duration);
        }
        if (testEnvironment) console.log(`${url}: ${duration} ms`);
    } else if (testEnvironment) {
        console.warn('No performance entries found for "api-call-duration"');
    }

    // Clear marks to prevent memory leaks
    performance.clearMarks(`${uniqueID}-start`);
    performance.clearMarks(`${uniqueID}-end`);
    performance.clearMeasures(`${uniqueID}-duration`);
}

// Handle API errors and extract meaningful information
export async function handleApiError(error: AxiosError<ApiError>, callingURL?: string, payload?: any): Promise<Error> {
    if (error.response) {
        const { status, data } = error.response;
        const errorMessage = data.Message ?? data.message ?? 'API Error';
    
        // Ensure errorMessage is a string
        let fallbackErrorResponse = errorMessage ?? 'No message received from API response';

        if (typeof fallbackErrorResponse === 'object') {
            fallbackErrorResponse = JSON.stringify(fallbackErrorResponse);
        }
    
        if (status === 404) {
            fallbackErrorResponse = 'Resource does not exist';
        }

        let message = `Response: ${fallbackErrorResponse} - JS Version: ${BUILD_VERSION ?? 'noversion'} - Source URL - ${window.location.href}`;
        const timeStamp = new Date().toISOString();
        if((callingURL?.includes('Booking') || callingURL?.includes('ContactUs')) && payload && payload!=='N/A'){
            const fullLog = extractPageDetails();
            message = ''
            if(fullLog)
                message = ` Complete Log: ${JSON.stringify(fullLog)} -`
            const encryptedPayload = await secureIt(payload, process.env.ENCRYPTION_PWD ?? "nopwd", timeStamp);
            message = `Response: ${fallbackErrorResponse} - Source URL: ${window.location.href} - ${message} FailedPayload: ${JSON.stringify(encryptedPayload.ciphertext)} - ver: ${encryptedPayload.iv}`;
        }
    
        const logData = {
            httpStatusCode: status.toString(),
            message: message,
            timeStamp: timeStamp,
            apiEndpoint: removeApiKey(callingURL ?? ''),
            deviceType: `${isTouchDevice() ? 'Mobile' : 'Desktop'} Device - UserAgent: ${navigator.userAgent}`
        };
    
        if (!isWhitelistedAPI({ url: callingURL ?? '' }))
            return new Error(errorMessage);
        callErrorLogger(logData);
        return new Error(errorMessage);
    } else if (error.request) {
        return new Error('No response received from the server');
    } else {
        return new Error('Error occurred while setting up the request');
    }
}
//call crypto
export async function secureIt(obj: any, password: string, salt: string) {
    const crypto = CryptoWrapper.getInstance();
    const { ciphertext, iv, salt: storedSalt } = await crypto.encryptObject(obj, password, salt);
    return { ciphertext, iv, salt: storedSalt };
}

export async function unlockIt(ciphertext: string, iv: string, password: string, salt: string):Promise<void> {
    const crypto = CryptoWrapper.getInstance();
    const decrypted = await crypto.decryptObject(ciphertext, iv, password, salt);
    console.log(`%c[Crypto]%c Decrypted data:`,
        'background:green; color:#f3f3f3; font-weight: bold; padding: 0 8px', '', decrypted);
}


// Call the performance logger
async function callPerformanceLogger(callingURL: string, latency: number) {
    const logData = {
        source: "js-api-latency",
        apiEndpoint: `${removeApiKey(callingURL)} - Source URL - ${window.location.href}`,
        latency: `${latency} ms - JS Version: ${BUILD_VERSION ?? 'noversion'}`,
        timeStamp: new Date(),
        deviceType: isTouchDevice() ? `Mobile Device - UserAgent: ${navigator.userAgent}` : `Desktop Device - UserAgent: ${navigator.userAgent}`
    };

    const testEnvironment = isTestEnvironment();
    sendLogRequest(`${window.location.origin}/${apiConfig.ApiLogperformanceEndpoint}`, logData, testEnvironment);
}

// Call error logger
export async function callErrorLogger(logData: any) {
    const testEnvironment = isTestEnvironment();

    //Uncomment the following line to send the error stack and change errorMessages: [logData] -> errorMessages: errors
    // const errorService = OnLoaderServices.getInstance();
    // const errors = errorService.getLoggedErrors();
    // errors.push(logData);

    const url = `${window.location.origin}/${apiConfig.ApiLogerrorEndpoint}`;
    if (!logData.apiEndpoint) {
        if (testEnvironment) console.log("Error in determining the API endpoint of the failed call");
        return;
    }

    if (logData.apiEndpoint !== url) {
        sendLogRequest(url, { source: "js-code", errorMessages: [logData] }, testEnvironment);
    } else if (testEnvironment) {
        console.error("Error Logger API failed.");
        console.log(logData);
    }
}

// Logger API request
export async function loggerRequest<T>(config: AxiosRequestConfig): Promise<T> {
    try {
        const response: AxiosResponse<T> = await loggerURLTransformer.request(config);
        return response.data;
    } catch (error: any) {
        throw await handleApiError(error, config.url);
    }
}

//performace mark fingerprint generator
export function uniqueIDGenerator(url:string){
    return `api-call-${url}-${generateRandomString(5)}`
}

   